import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Container,
  Alert,
  Stack,
  Center,
  Group,
  useMantineColorScheme,
  Box,
  Image,
  ActionIcon
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUser, IconLock, IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import authService from '../services/authService';
import { LoginRequest, UserRole } from '../types/auth';
import enarmLogo from '../assets/enarm_logo.png';
import { getGlassmorphismInputStyles, glassmorphismInputProps } from '../styles/inputStyles';
// import GoogleLoginButton from './GoogleLoginButton'; // REMOVIDO TEMPORALMENTE

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginRequest>({
    login: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  // No hacer redirección automática al cargar - solo al hacer login

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Limpiar error al escribir
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authService.login(credentials);
      console.log('Login exitoso:', response);
      console.log('Response completa:', JSON.stringify(response, null, 2));
      
      notifications.show({
        title: 'Inicio de Sesión Exitoso',
        message: 'Redirigiendo al panel...',
        color: 'teal',
        autoClose: 1500,
        styles: {
          root: {
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.9)' : 'rgba(247, 243, 238, 0.9)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
          },
          title: {
            color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
          },
          description: {
            color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
          }
        }
      });
      
      // Redirigir basado en el rol del usuario de la respuesta
      const { usuario } = response;
      console.log('Usuario objeto:', usuario);
      console.log('Usuario roles:', usuario?.roles);
      console.log('Tipo de roles:', Array.isArray(usuario?.roles) ? 'array' : typeof usuario?.roles);
      
      if (usuario?.roles) {
        console.log('Roles individuales:');
        if (Array.isArray(usuario.roles)) {
          usuario.roles.forEach((role, index) => {
            console.log(`Rol ${index}:`, role, `(tipo: ${typeof role})`);
          });
        } else {
          console.log('Roles no es un array:', usuario.roles);
        }
      }
      
      // Redirigir usando navigate con lógica mejorada
      const hasAdminRole = usuario?.roles?.includes(UserRole.ADMIN) || 
                          usuario?.roles?.includes('ADMIN');
      const hasEstudianteRole = usuario?.roles?.includes(UserRole.ESTUDIANTE) || 
                               usuario?.roles?.includes('ESTUDIANTE');
      
      console.log('hasAdminRole:', hasAdminRole);
      console.log('hasEstudianteRole:', hasEstudianteRole);
      console.log('UserRole.ADMIN:', UserRole.ADMIN);
      console.log('UserRole.ESTUDIANTE:', UserRole.ESTUDIANTE);
      
      if (hasAdminRole) {
        console.log('✅ Cumple condición ADMIN - Redirigiendo a admin dashboard');
        navigate('/admin/dashboard');
      } else if (hasEstudianteRole) {
        console.log('✅ Cumple condición ESTUDIANTE - Redirigiendo a estudiante dashboard');
        navigate('/estudiante/dashboard');
      } else {
        console.log('❌ No cumple ninguna condición específica - Redirigiendo a dashboard general');
        console.log('Roles recibidos:', usuario?.roles);
        navigate('/dashboard');
      }
      
    } catch (err: any) {
      console.error('Error en login:', err);
      
      let errorMessage = 'Error del servidor. Inténtalo de nuevo.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Credenciales incorrectas';
      } else if (err.response?.status === 423) {
        errorMessage = 'Usuario bloqueado';
      } else if (err.response?.status === 403) {
        errorMessage = 'Usuario deshabilitado';
      } else if (err.response?.data?.message) {
        // Si el backend envía un mensaje en español, usarlo
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      notifications.show({
        title: 'Error de Autenticación',
        message: errorMessage,
        color: 'red',
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          /* Smooth theme transitions */
          * {
            transition: background-color 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                        color 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                        border-color 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                        box-shadow 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                        backdrop-filter 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
          }
        `}
      </style>
      <Box
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Textura de fondo */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: colorScheme === 'dark'
            ? `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23374151" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`
            : `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23ddd8d1" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`,
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Back to Landing Button */}
      <ActionIcon
        onClick={() => navigate('/')}
        variant="light"
        size="lg"
        radius="xl"
        style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          zIndex: 10,
          backgroundColor: colorScheme === 'dark'
            ? 'rgba(30, 41, 59, 0.8)'
            : 'rgba(247, 243, 238, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
          boxShadow: colorScheme === 'dark'
            ? '0 4px 16px rgba(0, 0, 0, 0.3)'
            : '0 4px 16px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <IconArrowLeft size={20} />
      </ActionIcon>

      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
        <Container size={380} style={{ width: '100%', maxWidth: '380px' }}>
        <Paper
          radius="xl"
          px="lg"
          py={56}
          withBorder
          style={{
            width: '100%',
            backgroundColor: colorScheme === 'dark'
              ? 'rgba(30, 41, 59, 0.7)'
              : 'rgba(247, 243, 238, 0.6)',
            backdropFilter: 'blur(40px) saturate(200%)',
            WebkitBackdropFilter: 'blur(40px) saturate(200%)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
            boxShadow: colorScheme === 'dark'
              ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          }}
        >
          <Stack gap="xl">
            <Center>
              <Stack align="center" gap="md">
                <Image
                  src={enarmLogo}
                  alt="ENARM360 Logo"
                  height={65}
                  fit="contain"
                  style={{
                    borderRadius: '12px',
                    maxWidth: '200px',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate('/')}
                />
                <Text size="sm" c="dimmed" ta="center">
                  Simulador Médico Profesional
                </Text>
              </Stack>
            </Center>

            <form onSubmit={handleSubmit}>
              <Stack gap="lg">
                <TextInput
                  placeholder="Usuario o correo electrónico"
                  name="login"
                  value={credentials.login}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  leftSection={<IconUser size={16} />}
                  {...glassmorphismInputProps}
                  styles={getGlassmorphismInputStyles(colorScheme)}
                />

                <PasswordInput
                  placeholder="Contraseña"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  leftSection={<IconLock size={16} />}
                  {...glassmorphismInputProps}
                  styles={getGlassmorphismInputStyles(colorScheme)}
                />

                <Group justify="flex-end">
                  <Text
                    size="sm"
                    style={{
                      color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                    }}
                    onClick={() => navigate('/forgot-password')}
                  >
                    ¿Olvidaste tu contraseña?
                  </Text>
                </Group>

                {error && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="red"
                    radius="xl"
                    variant="light"
                    style={{
                      backgroundColor: colorScheme === 'dark'
                        ? 'rgba(239, 68, 68, 0.1)'
                        : 'rgba(239, 68, 68, 0.05)',
                      backdropFilter: 'blur(20px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                      border: `1px solid rgba(239, 68, 68, 0.2)`,
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  disabled={!credentials.login || !credentials.password}
                  size="md"
                  radius="xl"
                  variant="light"
                  style={{
                    marginTop: '0.5rem',
                    backgroundColor: colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.8)'
                      : 'rgba(247, 243, 238, 0.8)',
                    backdropFilter: 'blur(40px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(200%)',
                    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(221, 216, 209, 0.8)'}`,
                    boxShadow: colorScheme === 'dark'
                      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
                      : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                    fontWeight: 600,
                    letterSpacing: '0.025em',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
              </Stack>
            </form>

            <Center>
              <Text size="sm" c="dimmed">
                ¿No tienes una cuenta?{' '}
                <Text
                  component="span"
                  size="sm"
                  c="blue"
                  style={{ cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => navigate('/register')}
                >
                  Regístrate aquí
                </Text>
              </Text>
            </Center>
          </Stack>
        </Paper>
        </Container>
      </div>
    </Box>
    </>
  );
};

export default Login;